// src/components/ui/Modal.tsx

import { ReactNode, useEffect, useRef, useCallback } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { createPortal } from 'react-dom';
import { isTopmostModalDialog } from './topmostDialog';
import { X } from 'lucide-react';
import Button from './Button';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: ReactNode;
  footer?: ReactNode;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  /**
   * Id of the element that names the dialog, for callers that render their own
   * heading instead of passing `title` (ConfirmDialog). Without a name a screen
   * reader announces only "dialog".
   */
  labelledBy?: string;
  /** Id of the element that describes the dialog, likewise. */
  describedBy?: string;
}

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-4xl',
};

export default function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  labelledBy,
  describedBy,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // Tab stays inside the open dialog (see useFocusTrap).
  useFocusTrap(modalRef, isOpen);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Only the topmost modal answers Escape (see topmostDialog.ts).
      if (e.key === 'Escape' && closeOnEscape && isTopmostModalDialog(rootRef.current)) {
        onClose();
      }
    },
    [onClose, closeOnEscape]
  );

  // Store latest handleKeyDown in a ref so the effect doesn't re-run
  // on every render (which would steal focus from inputs inside the modal).
  const handleKeyDownRef = useRef(handleKeyDown);
  handleKeyDownRef.current = handleKeyDown;

  // Focus trap and keyboard handling — only re-run when isOpen changes
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElement.current = document.activeElement as HTMLElement;
      const keyHandler = (e: KeyboardEvent) => handleKeyDownRef.current(e);
      document.addEventListener('keydown', keyHandler);
      document.body.style.overflow = 'hidden';

      // Focus the modal on open
      setTimeout(() => {
        modalRef.current?.focus();
      }, 0);

      return () => {
        document.removeEventListener('keydown', keyHandler);
        document.body.style.overflow = '';
        previouslyFocusedElement.current?.focus();
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      ref={rootRef}
      // THE OVERLAY LAYER SCROLLS, not the panel. The panel has no height cap,
      // so a dialog taller than the viewport used to be clipped top and bottom
      // with page scroll locked — its footer/actions were unreachable (the
      // wizard's "Skip" at 720px). Scrolling this layer reaches every part of
      // the panel without making the panel itself a clipping container, so
      // dropdowns/popovers inside a dialog are not cut off.
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy ?? (title ? 'modal-title' : undefined)}
      aria-describedby={describedBy ?? (description ? 'modal-description' : undefined)}
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Centring layer: at least the viewport tall, so short dialogs stay
          centred and tall ones grow it and scroll. A click on it (outside the
          panel) is an overlay click. */}
      <div
        data-modal-centering
        className="relative flex min-h-full items-center justify-center p-4"
        onClick={handleOverlayClick}
      >
        {/* Modal panel */}
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`
            relative bg-white rounded-xl shadow-xl w-full
            transform transition-all
            focus:outline-none
            ${sizeClasses[size]}
          `}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-start justify-between p-4 border-b border-gray-200">
              <div>
                {title && (
                  <h2
                    id="modal-title"
                    className="text-lg font-semibold text-gray-900"
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p
                    id="modal-description"
                    className="mt-1 text-sm text-gray-500"
                  >
                    {description}
                  </p>
                )}
              </div>
              {showCloseButton && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label="Close modal"
                  className="-mr-2 -mt-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              )}
            </div>
          )}

          {/* Body */}
          <div className="p-4">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// Convenience components for common modal patterns
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  isLoading = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'destructive';
  isLoading?: boolean;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant}
            onClick={onConfirm}
            isLoading={isLoading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-gray-600">{message}</p>
    </Modal>
  );
}
